import type { OnInit } from '@angular/core';
import {
  Component,
  ViewContainerRef,
  afterNextRender,
  computed,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { isEqual, isString, omit } from 'lodash';
import { DateTime } from 'luxon';
import { take } from 'rxjs';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxDropdownOverlayService } from '../../../internal/services/onyx-dropdown-overlay.service';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';
import { WarningType } from '../enums';
import type { OnyxDate, OnyxDatepickerYearsRange } from '../interfaces';
import { ONYX_DATEPICKER_CONFIG } from '../interfaces';
import { OnyxDatepickerCalendarsComponent } from './onyx-datepicker-calendars/onyx-datepicker-calendars.component';
import { OnyxDatepickerInputComponent } from './onyx-datepicker-input/onyx-datepicker-input.component';

@Component({
  selector: 'onyx-datepicker',
  imports: [
    OnyxDatepickerInputComponent,
    OnyxErrorComponent,
    OnyxIconComponent,
    TranslatePipe,
  ],
  templateUrl: './onyx-datepicker.component.html',
  styleUrl: './onyx-datepicker.component.scss',
  host: { '[style.width]': `width()` },
  providers: [
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxDatepickerComponent),
    },
  ],
})
export class OnyxDatepickerComponent<T extends OnyxDate | string>
  extends OnyxBaseFormControlComponent
  implements FormValueControl<T | null>, OnInit
{
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly onyxDropdownService = inject(OnyxDropdownOverlayService);

  public readonly value = model<T | null>(null);
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly size = input<32 | 40>(40);
  public readonly labelTo = input<string>();
  public readonly showDateRange = input(false);
  public readonly showIndefinite = input(false);
  public readonly indefiniteLabel = input<string>();
  public readonly yearsRange = input<OnyxDatepickerYearsRange>({
    past: 20,
    future: 20,
  });
  public readonly forceError = input(false);
  public readonly showRequired = input(true);
  public readonly showClear = input(true);
  public readonly showYear = input(true);
  public readonly showErrors = input(true);
  public readonly warningTypes = input<`${WarningType}`[]>([]);
  public readonly width = input('100%');

  public readonly rangeValueChange = output<OnyxDate | null>();

  protected readonly I18N = `${I18N_NAMESPACE}.datepicker`;

  protected _value = signal<OnyxDate>({});
  protected hasError = signal(false);

  private previousValue = signal<T | null>(null);

  protected warning = computed(() => {
    const types = this.warningTypes();
    if (!types.length || this.disabled()) return null;

    const value = this._value();

    for (const type of types) {
      switch (type) {
        case WarningType.PAST_DATE:
          if (this.isPast(value)) return `${this.I18N}.warnings.${type}`;
          break;
      }
    }

    return null;
  });

  private datepickerInputFromComponentRef =
    viewChild.required<OnyxDatepickerInputComponent>(
      'datepickerInputFromComponent',
    );
  private datepickerInputToComponentRef =
    viewChild<OnyxDatepickerInputComponent>('datepickerInputToComponent');
  private allowFocusout = signal(false);
  private isCalendarAttached = signal(false);

  public override ngOnInit(): void {
    super.ngOnInit();

    toObservable(this.value, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (isEqual(value, this.previousValue())) return;

        this.previousValue.set(value);
        this.transformValue(value);
      });
  }

  public focus(): void {
    if (this.disabled()) return;

    this.datepickerInputFromComponentRef().focus();
  }

  protected changeFromValue(value: {
    date: string | null;
    indefinite: boolean | null;
  }): void {
    this.changeValue({
      ...this._value(),
      from: value.date,
      indefinite: value.indefinite ?? false,
    });
  }

  protected changeToValue(value: {
    date: string | null;
    indefinite: boolean | null;
  }): void {
    this.changeValue({
      ...this._value(),
      to: value.date,
      indefinite: value.indefinite ?? false,
    });
  }

  protected enableFocusout(): void {
    this.allowFocusout.set(true);
  }

  protected focusoutCloseFromCalendar(): void {
    if (this.showDateRange()) return;

    this.focusoutCloseCalendar();
  }

  protected focusoutCloseCalendar(): void {
    setTimeout(() => {
      if (this.allowFocusout() && this.isCalendarAttached()) {
        this.onyxDropdownService.detachDropdown();
        this.touch.emit();
      }
    }, 100);
  }

  protected openCalendarAndFocus(
    event: Event,
    inputComponentRef: OnyxDatepickerInputComponent,
  ): void {
    event.stopPropagation();
    if (
      this.disabled() ||
      (this._value().indefinite && !this.showDateRange())
    ) {
      return;
    }

    inputComponentRef.focusInputs(event);
    this.allowFocusout.set(false);
    this.attachCalendar();
  }

  private attachCalendar(): void {
    this.onyxDropdownService.attachDropdown(
      this.datepickerInputFromComponentRef().inputElementRef(),
      this.viewContainerRef,
      OnyxDatepickerCalendarsComponent,
      ONYX_DATEPICKER_CONFIG,
      {
        date: this._value,
        changeDate: (value: OnyxDate | null) => this.changeCalendarDate(value),
        disableFocusout: () => this.allowFocusout.set(false),
        showDateRange: this.showDateRange(),
        yearsRange: this.yearsRange(),
        width: 'flex',
      },
    );

    this.isCalendarAttached.set(true);
    this.onyxDropdownService.detach$
      .pipe(take(1))
      .subscribe(() => this.isCalendarAttached.set(false));
  }

  private changeCalendarDate(value: OnyxDate | null): void {
    value = {
      from: value?.from ?? null,
      to: value?.to ?? null,
      indefinite: value?.to ? false : this._value().indefinite,
    };
    this.changeValue(value);

    this.datepickerInputFromComponentRef().setValue(
      value?.from,
      this.showDateRange() ? false : value?.indefinite,
    );
    this.datepickerInputToComponentRef()?.setValue(
      value?.to,
      value?.indefinite,
    );
  }

  private changeValue(value: OnyxDate): void {
    this._value.set(value);
    value = {
      from: value?.from ?? null,
      to: value?.to ?? null,
      indefinite: value?.indefinite ?? false,
    };

    const formValue = this.showDateRange()
      ? this.showIndefinite()
        ? value
        : omit(value, 'indefinite')
      : this.showIndefinite()
        ? { date: value.from, indefinite: value.indefinite }
        : (value.from ?? null);

    this.previousValue.set(formValue as T | null);
    this.value.set(formValue as T | null);
    this.touch.emit();

    if (this.showDateRange()) this.rangeValueChange.emit(value ?? null);
  }

  private transformValue(value: T | null): void {
    afterNextRender(
      {
        write: () => {
          if (!value) {
            this._value.set({});
            this.datepickerInputFromComponentRef().setValue(null);
            this.datepickerInputToComponentRef()?.setValue(null);
          } else if (isString(value)) {
            this._value.set({ from: value, indefinite: false });
            this.datepickerInputFromComponentRef().setValue(value);
          } else {
            const date: OnyxDate = {
              from: value.date ? value.date : value.from ? value.from : null,
              to: value.to ? value.to : null,
              indefinite: value.indefinite ?? false,
            };
            this._value.set(date);
            this.datepickerInputFromComponentRef().setValue(
              date.from,
              date.indefinite,
            );
            this.datepickerInputToComponentRef()?.setValue(
              date.to,
              date.indefinite,
            );
          }
        },
      },
      { injector: this.injector },
    );
  }

  private isPast(value: OnyxDate): boolean {
    const now = DateTime.now().startOf('day');

    if (this.showDateRange()) {
      const dateToCheck = value.to || value.from;
      if (!dateToCheck) return false;
      return DateTime.fromISO(dateToCheck).startOf('day') < now;
    }

    const date = value.date ?? value.from ?? null;
    return date ? DateTime.fromISO(date).startOf('day') < now : false;
  }
}
